import React, { useState, useCallback, useMemo } from "react";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiFileText, FiTag } from "react-icons/fi";
import { InfoRow, SearchableSelect, SimpleSelect } from "../../_components/Sales/SalesShared";
import { getCNById, updateCreditNote, createCreditNote, deleteCreditNote } from "../../_services/finance.service";
import { getInvoices } from "../../_services/sales.service";
import { INVOICE_OPTIONS_PAGE_SIZE, customerInvoices, invoiceOptions } from "./salesNoteShared";
import { getActiveCustomerCompanyNames, getCustomersByName } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

// ─── Constants ───────────────────────────────────────────────────────────────
const REASONS = [
    "Defective / Damaged",
    "Pricing Correction",
    "Order Cancelled",
    "Customer Return"
];

async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCustomersByName(customerName);
    return data
        .filter(r => r.name === customerName && !!r.attention && (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.attention)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchCustomerDetails(customerName) {
    if (!customerName) return {};
    const data = await getCustomersByName(customerName);
    const match = data.find(r => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || "",
        customerAddress: match.billingAddress || match.address || match.shippingAddress || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function CreditNoteDetails() {
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const fetchInvoices = useCallback((customerName, contactPerson) => {
        if (!customerName) {
            setInvoices([]);
            setSelectedInvoice(null);
            return;
        }
        getInvoices({ pageSize: INVOICE_OPTIONS_PAGE_SIZE, page: 1, search: customerName, searchType: "customer", status: "Approved" })
            .then((res) => setInvoices(customerInvoices(res?.data || [], customerName, contactPerson)))
            .catch(() => setInvoices([]));
    }, []);

    const handleInvoiceSelect = useCallback((invoiceId) => {
        const found = invoices.find(inv => inv.id === invoiceId);
        setSelectedInvoice(found || null);
    }, [invoices]);

    const fillFromInvoice = useMemo(() => {
        if (!selectedInvoice) return null;
        return {
            customerAddress: selectedInvoice.customerAddress || "",
            items: (selectedInvoice.items || []).map(item => ({
                ...item,
                selected: true,
            })),
            invoiceId: selectedInvoice.id,
            // The items' prices are in the invoice's currency, so the note
            // takes it (and the invoice's rate) — a USD invoice isn't saved as MYR.
            ...(selectedInvoice.currency
                ? { currency: selectedInvoice.currency, exchangeRate: selectedInvoice.exchangeRate ?? null }
                : {}),
        };
    }, [selectedInvoice]);

    const cnExtra = useMemo(() => ({
        left: ({ draft, onChange, errors, edit, invoices: availableInvoices }) =>
            edit ? (
                <>
                    <SimpleSelect
                        label="Invoice Ref. No."
                        name="invoiceId"
                        value={draft?.invoiceId || ""}
                        onChange={(e) => {
                            onChange(e);
                            handleInvoiceSelect(e.target.value);
                        }}
                        options={invoiceOptions(availableInvoices, draft?.invoiceId)}
                        placeholder="Select Invoice (Approved)..."
                        disabled={!draft?.customer || !draft?.contactPerson || invoiceOptions(availableInvoices, draft?.invoiceId).length === 0}
                        required
                        error={errors?.invoiceId}
                    />
                    <SearchableSelect
                        label="Reason for Credit"
                        name="reason"
                        value={draft?.reason || ""}
                        onChange={onChange}
                        options={REASONS}
                        placeholder="Type or select a reason..."
                        required
                        disabled={!draft?.invoiceId}
                        error={errors?.reason}
                        allowFreeText={true}
                    />
                </>
            ) : (
                <>
                    {(draft?.invoiceId || draft?.invoice_id || draft?.invoiceRef) && (
                        <InfoRow icon={FiFileText} label="Invoice Ref.">
                            {draft.invoiceId || draft.invoice_id || draft.invoiceRef}
                        </InfoRow>
                    )}
                    {draft?.reason && (
                        <InfoRow icon={FiTag} label="Credit Reason">{draft.reason}</InfoRow>
                    )}
                </>
            ),
        right: null,
    }), [handleInvoiceSelect]);

    const fetchCustDetails = useCallback((name) => fetchCustomerDetails(name), []);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <SalesDocDetails
            docType="Credit Note"
            basePath="/finance/credit-notes"
            fetchById={getCNById}
            updateItem={updateCreditNote}
            createItem={createCreditNote}
            deleteItem={deleteCreditNote}
            fetchCustomerNames={getActiveCustomerCompanyNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ invoiceId: "", reason: "" }}
            extraFields={cnExtra}
            extraFieldProps={{ invoices }}
            hidePaymentTerms={true}
            addressLabel="Customer Address"
            addressField="customerAddress"
            fillDraft={fillFromInvoice}
            onDependentFieldChange={fetchInvoices}
            onBeforeSave={(d) => ({ ...d, paymentTerms: "" })}
            forceNegativeTotals={true}
            totalLabel="Credit Amount"
            lineItemProps={{
                selectable: true,
                noPrice: false,
            }}
        />
    );
}